// src/components/chatbot/chatbotService.ts
import { SuggestionItem } from './types';

// Default suggestions for initial message
export const defaultSuggestions: SuggestionItem[] = [
  { id: 'project', text: 'Tell me about projects' },
  { id: 'team', text: 'How to manage teams?' },
  { id: 'time', text: 'Time tracking help' },
  { id: 'clients', text: 'Client management' },
  { id: 'reports', text: 'How to view reports?' },
];

type BotResponse = {
  text: string;
  suggestions: SuggestionItem[];
};

// Get bot response based on user message
export const getBotResponse = (userMessage: string): string | BotResponse => {
  const normalizedMessage = userMessage.toLowerCase();
  
  if (normalizedMessage.includes('hello') || normalizedMessage.includes('hi') || normalizedMessage.includes('hey')) {
    return {
      text: "Hello! How can I assist you with Staffluent today?",
      suggestions: defaultSuggestions,
    };
  }
  
  if (normalizedMessage.includes('help')) {
    return {
      text: "I can help you with managing teams, tracking projects, time & attendance, quality control, and more. What specific area do you need assistance with?",
      suggestions: defaultSuggestions,
    };
  }
  
  if (normalizedMessage.includes('project') || normalizedMessage.includes('projects')) {
    return {
      text: "Staffluent offers comprehensive project management features. You can create projects, assign teams, track progress, and manage tasks.",
      suggestions: [
        { id: 'create-project', text: 'Create a new project' },
        { id: 'assign-team', text: 'Assign team to project' },
        { id: 'track-progress', text: 'Track project progress' },
        { id: 'project-reports', text: 'Project reports' },
      ],
    };
  }
  
  if (normalizedMessage.includes('time') || normalizedMessage.includes('attendance') || normalizedMessage.includes('clock')) {
    return {
      text: "Our time & attendance system allows you to clock in/out, track breaks, and manage timesheets. Team leaders can approve time entries and view reports.",
      suggestions: [
        { id: 'clock-in', text: 'Clock in/out' },
        { id: 'breaks', text: 'Manage breaks' },
        { id: 'timesheets', text: 'View timesheets' },
        { id: 'approval', text: 'Approval workflow' },
      ],
    };
  }
  
  if (normalizedMessage.includes('team') || normalizedMessage.includes('staff')) {
    return {
      text: "Staffluent makes team management easy. You can organize staff into departments and teams, assign team leaders, and track performance.",
      suggestions: [
        { id: 'create-team', text: 'Create a new team' },
        { id: 'assign-leader', text: 'Assign team leader' },
        { id: 'track-performance', text: 'Track performance' },
        { id: 'team-scheduling', text: 'Team scheduling' },
      ],
    };
  }
  
  if (normalizedMessage.includes('client') || normalizedMessage.includes('customer')) {
    return {
      text: "You can manage all your client relationships in Staffluent. Add client details, track communications, and link clients to projects. The client portal allows clients to view project status and more.",
      suggestions: [
        { id: 'add-client', text: 'Add a new client' },
        { id: 'client-portal', text: 'Client portal features' },
        { id: 'client-comms', text: 'Client communication' },
        { id: 'invoicing', text: 'Invoicing clients' },
      ],
    };
  }
  
  if (normalizedMessage.includes('report') || normalizedMessage.includes('analytics')) {
    return {
      text: "Staffluent provides detailed analytics and reporting features. You can view productivity metrics, project progress, task completion rates, and more.",
      suggestions: [
        { id: 'performance-report', text: 'Performance reports' },
        { id: 'project-report', text: 'Project reports' },
        { id: 'time-report', text: 'Time & attendance reports' },
        { id: 'export-data', text: 'Export data' },
      ],
    };
  }
  
  if (normalizedMessage.includes('task') || normalizedMessage.includes('todo')) {
    return {
      text: "Tasks in Staffluent can be created, assigned to team members, prioritized, and tracked to completion. They can be standalone or part of larger projects.",
      suggestions: [
        { id: 'create-task', text: 'Create a task' },
        { id: 'assign-task', text: 'Assign tasks' },
        { id: 'task-priority', text: 'Set task priority' },
        { id: 'track-tasks', text: 'Track task completion' },
      ],
    };
  }
  
  // Default response if no keywords match
  return {
    text: "I understand you're asking about \"" + userMessage + "\". Could you provide more details so I can assist you better?",
    suggestions: [
      { id: 'help', text: 'Show all help topics' },
      ...defaultSuggestions.slice(0, 3)
    ],
  };
};

// Save messages to localStorage
export const saveMessages = (messages: any[], storageKey: string): void => {
  try {
    localStorage.setItem(storageKey, JSON.stringify(messages));
  } catch (error) {
    console.error('Failed to save messages:', error);
  }
};

// Load messages from localStorage
export const loadMessages = (storageKey: string): any[] | null => {
  try {
    const savedMessages = localStorage.getItem(storageKey);
    if (savedMessages) {
      return JSON.parse(savedMessages);
    }
    return null;
  } catch (error) {
    console.error('Failed to load messages:', error);
    return null;
  }
};